/**
 * Feature flags for files-viewer.
 *
 * Centralised toggles for in-development features that ship to main but
 * stay invisible to end users until they are ready. Backend routes for
 * gated features remain mounted and dormant; only their entry points in
 * the UI are hidden.
 *
 * To enable a flag locally, set the matching `VITE_*` variable in your
 * `.env.local`:
 *
 *     VITE_WORKFLOW_ENGINE_ENABLED=true
 */

const env = import.meta.env;

function readBool(value: unknown, defaultValue = false): boolean {
    if (value == null) {
        return defaultValue;
    }
    return value === 'true' || value === true;
}

export const FEATURE_FLAGS = {
    /**
     * Workflow engine UI surface:
     *   - Work Queue admin page (sidebar nav at `/work-queue`)
     *   - "Create Dx report" patient action (in ActionsTab)
     *   - Per-step task surfaces wired to those entry points
     *
     * Backend `/api/v2/workflows`, `/api/v2/admin`, `/api/v2/patients/:id/workflow/*`,
     * and `/api/v2/patients/:id/clinical/dx-report/*` remain mounted regardless.
     */
    WORKFLOW_ENGINE_ENABLED: readBool(env.VITE_WORKFLOW_ENGINE_ENABLED),

    /**
     * Spark refinement wizard on the "Create refinement in manufacturer" task.
     * Enabled by default; a build can set the var to "false" to fall back to
     * the manual confirmation flow even for Spark practices with a usable
     * integration.
     */
    SPARK_REFINEMENT_WIZARD_ENABLED: readBool(env.VITE_SPARK_REFINEMENT_WIZARD_ENABLED, true),

    /**
     * Spark submission (create-case) wizard on the "Submit to manufacturer" task.
     * Dark launch / opt-in: disabled unless a build sets the var to "true"; when
     * disabled the task shows the manual submission fallback even for Spark
     * practices with a usable integration.
     */
    SPARK_CREATE_CASE_WIZARD_ENABLED: readBool(env.VITE_SPARK_CREATE_CASE_WIZARD_ENABLED, true)
} as const;

export type FeatureFlag = keyof typeof FEATURE_FLAGS;
